import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  signOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebaseConfig';
import {
  requireConnectionForRemoteAccess,
  setAccountDeletionMutationBlock,
} from './connectivityService';
import { clearPendingInvite } from './authService';

export const ACCOUNT_DELETION_STATES = Object.freeze({
  IDLE: 'idle',
  PREFLIGHT: 'preflight',
  BLOCKED: 'blocked',
  REAUTH: 'reauth',
  DELETING: 'deleting',
  PENDING: 'partial/pending',
  SUCCESS: 'success',
  ERROR: 'error',
});

export class AccountDeletionError extends Error {
  constructor(code, details) { super(code); this.name = 'AccountDeletionError'; this.code = code; this.details = details; }
}

let inFlight = null;
let snapshot = { uid: null, state: ACCOUNT_DELETION_STATES.IDLE, pending: false, canResume: false, step: null };
const listeners = new Set();

const publish = (uid, value) => {
  snapshot = { uid, ...value };
  setAccountDeletionMutationBlock(Boolean(value.pending));
  listeners.forEach(listener => listener());
  return snapshot;
};

export const subscribeAccountDeletion = (listener) => { listeners.add(listener); return () => listeners.delete(listener); };
export const getAccountDeletionSnapshot = () => snapshot;
export const resetAccountDeletionSnapshot = () => publish(null, {
  state: ACCOUNT_DELETION_STATES.IDLE, pending: false, canResume: false, step: null,
});

const codeOf = error => String(error?.code || '');
const reasonOf = error => String(error?.details?.reason || error?.details?.details?.reason || '');
const normalize = (error) => {
  if (error instanceof AccountDeletionError) return error;
  const code = codeOf(error);
  const reason = reasonOf(error);
  if (code.includes('wrong-password') || code.includes('invalid-credential')) return new AccountDeletionError('PASSWORD_INCORRECT');
  if (code.includes('requires-recent-login') || reason === 'REAUTHENTICATION_REQUIRED') return new AccountDeletionError('REAUTHENTICATION_REQUIRED');
  if (reason === 'OWNED_ACCOUNT_HAS_OTHER_MEMBERS') return new AccountDeletionError(reason, error?.details);
  if (reason === 'ACCOUNT_OWNERSHIP_INCONSISTENT') return new AccountDeletionError(reason, error?.details);
  if (code.includes('unauthenticated') || code.includes('user-not-found')) return new AccountDeletionError('AUTH_REQUIRED');
  if (code.includes('already-exists')) return new AccountDeletionError('ACCOUNT_DELETION_IN_PROGRESS');
  if (code === 'OFFLINE_REQUIRED' || error?.message === 'OFFLINE_REQUIRED') return new AccountDeletionError('OFFLINE_REQUIRED');
  return new AccountDeletionError('ACCOUNT_DELETION_FAILED', error);
};

function currentPasswordUser() {
  const user = auth.currentUser;
  if (!user?.uid || !user.email) throw new AccountDeletionError('AUTH_REQUIRED');
  if (!user.providerData.some(provider => provider.providerId === 'password')) {
    throw new AccountDeletionError('UNSUPPORTED_AUTH_PROVIDER');
  }
  return user;
}

export function hasSupportedAccountDeletionProvider() {
  const user = auth.currentUser;
  return Boolean(user?.email && user.providerData.some(provider => provider.providerId === 'password'));
}

export async function getCurrentAccountDeletionState() {
  const user = auth.currentUser;
  if (!user) { resetAccountDeletionSnapshot(); return snapshot; }
  try {
    requireConnectionForRemoteAccess();
    const callable = httpsCallable(functions, 'getAccountDeletionState');
    const result = (await callable()).data;
    return publish(user.uid, result);
  } catch (error) { throw normalize(error); }
}

export async function inspectCurrentAccountDeletion() {
  const user = auth.currentUser;
  if (!user) throw new AccountDeletionError('AUTH_REQUIRED');
  try {
    requireConnectionForRemoteAccess();
    return (await httpsCallable(functions, 'getAccountDeletionEligibility')()).data;
  } catch (error) { throw normalize(error); }
}

async function execute(password) {
  const user = currentPasswordUser();
  const uid = user.uid;
  requireConnectionForRemoteAccess();
  const eligibility = await inspectCurrentAccountDeletion();
  if (!eligibility.eligible) throw new AccountDeletionError('OWNED_ACCOUNT_HAS_OTHER_MEMBERS', eligibility);
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password))
    .catch(error => { throw normalize(error); });
  await user.getIdToken(true).catch(error => { throw normalize(error); });
  if (auth.currentUser?.uid !== uid) throw new AccountDeletionError('AUTH_REQUIRED');

  publish(uid, { state: ACCOUNT_DELETION_STATES.DELETING, pending: true, canResume: false, step: null });
  try {
    const result = await httpsCallable(functions, 'deleteNaraAccount')();
    if (!result.data?.deleted) throw new AccountDeletionError('ACCOUNT_DELETION_FAILED');
    if (auth.currentUser?.uid === uid) {
      publish(uid, { state: ACCOUNT_DELETION_STATES.SUCCESS, pending: false, canResume: false, step: null });
      setAccountDeletionMutationBlock(true);
      clearPendingInvite();
      await signOut(auth).catch(() => undefined);
    }
  } catch (error) {
    if (auth.currentUser?.uid === uid) {
      try { await getCurrentAccountDeletionState(); }
      catch (_) { publish(uid, { state: ACCOUNT_DELETION_STATES.PENDING, pending: true, canResume: true, step: null }); }
    }
    throw normalize(error);
  }
}

export function deleteCurrentNaraAccount(password) {
  if (inFlight) return Promise.reject(new AccountDeletionError('ACCOUNT_DELETION_IN_PROGRESS'));
  const operation = execute(password);
  inFlight = operation;
  void operation.finally(() => { if (inFlight === operation) inFlight = null; }).catch(() => undefined);
  return operation;
}

export function getAccountDeletionErrorMessage(error) {
  const code = normalize(error).code;
  if (code === 'OFFLINE_REQUIRED') return 'A exclusão exige conexão. Conecte-se e tente novamente.';
  if (code === 'PASSWORD_INCORRECT') return 'A senha atual está incorreta.';
  if (code === 'REAUTHENTICATION_REQUIRED') return 'Sua autenticação expirou. Confirme a senha novamente.';
  if (code === 'UNSUPPORTED_AUTH_PROVIDER') return 'A exclusão pela web ainda exige uma conta autenticada com email e senha.';
  if (code === 'OWNED_ACCOUNT_HAS_OTHER_MEMBERS') return 'Você é proprietário de uma conta com outras pessoas. Remova os demais membros ou resolva a propriedade antes de excluir sua conta.';
  if (code === 'ACCOUNT_OWNERSHIP_INCONSISTENT') return 'A exclusão foi interrompida para proteger dados de terceiros. Entre em contato com o suporte.';
  if (code === 'ACCOUNT_DELETION_IN_PROGRESS') return 'A exclusão já está em andamento.';
  if (code === 'AUTH_REQUIRED') return 'Sua sessão não é mais válida. Entre novamente.';
  return 'A exclusão não terminou. Seus dados financeiros permanecem bloqueados; autentique-se novamente para retomar com segurança.';
}
